import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Box, Button, Typography, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { useMutation } from '@apollo/client/react';
import { displayNotification } from '@/utils/notifications';
import {
  SelectFreePlanDocument,
  CreateCheckoutSessionDocument,
  VerifyCheckoutSessionDocument,
} from '@/graphql/generated/graphql';
import CircularLoader from '@/components/Loaders/CircularLoader';

interface PlanSelectionStepProps {
  onComplete: () => void;
}

const PlanSelectionStep: React.FC<PlanSelectionStepProps> = ({ onComplete }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [interval, setInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [verifying, setVerifying] = useState(false);

  const [selectFreePlan, { loading: selectingFree }] = useMutation(SelectFreePlanDocument);
  const [createCheckoutSession, { loading: creatingCheckout }] = useMutation(CreateCheckoutSessionDocument);
  const [verifyCheckoutSession] = useMutation(VerifyCheckoutSessionDocument);

  const sessionId = searchParams.get('session_id');
  const cancelled = searchParams.get('checkout') === 'cancelled';

  const clearCheckoutParams = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('session_id');
    next.delete('checkout');
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (!sessionId) return;

    setVerifying(true);
    verifyCheckoutSession({
      variables: { sessionId },
      onCompleted: () => {
        setVerifying(false);
        onComplete();
      },
      onError: (error) => {
        setVerifying(false);
        displayNotification({
          type: 'error',
          text: error.message || 'Could not confirm payment. Please try again.',
        });
        clearCheckoutParams();
      },
    });
  }, [sessionId]);

  useEffect(() => {
    if (cancelled) {
      displayNotification({ type: 'info', text: 'Checkout was cancelled.' });
      clearCheckoutParams();
    }
  }, [cancelled]);

  const handleFree = () => {
    selectFreePlan({
      variables: {},
      onCompleted: () => onComplete(),
      onError: (error) => displayNotification({ type: 'error', text: error.message || 'Something went wrong. Please try again.' }),
    });
  };

  const handlePaid = () => {
    createCheckoutSession({
      variables: { interval, returnPath: '/onboarding?onboarding_step=plan' },
      onCompleted: (data) => {
        if (data.createCheckoutSession) {
          const url = new URL(window.location.href);
          url.searchParams.set('onboarding_step', 'plan');
          window.history.replaceState(null, '', url.toString());
          window.location.href = data.createCheckoutSession;
        } else {
          displayNotification({ type: 'error', text: 'Could not start checkout. Please try again.' });
        }
      },
      onError: (error) => displayNotification({ type: 'error', text: error.message || 'Could not start checkout. Please try again.' }),
    });
  };

  if (verifying) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 6 }}>
        <CircularLoader />
        <Typography variant="body2" color="text.secondary">
          Confirming your payment...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="body2" color="text.secondary">
        Choose a plan to finish setting up your workspace.
      </Typography>

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>Free</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Basic features, no card required.
        </Typography>
        <Button variant="outlined" fullWidth onClick={handleFree} disabled={selectingFree}>
          {selectingFree ? 'Setting up...' : 'Continue with Free'}
        </Button>
      </Box>

      <Box sx={{ border: 1, borderColor: 'primary.main', borderRadius: 2, p: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>Paid</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Full access to all features.
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={interval}
          onChange={(_, value) => value && setInterval(value)}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="monthly">£10 / Month</ToggleButton>
          <ToggleButton value="yearly">£120 / Year</ToggleButton>
        </ToggleButtonGroup>
        <Button variant="contained" fullWidth onClick={handlePaid} disabled={creatingCheckout}>
          {creatingCheckout ? 'Redirecting...' : `Subscribe ${interval === 'monthly' ? 'Monthly' : 'Yearly'}`}
        </Button>
      </Box>
    </Box>
  );
};

export default PlanSelectionStep;
